import { MapsAPILoader } from "@agm/core";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor
} from "@angular/forms";
import { Subject } from "rxjs";
import { MapService } from "../map.service";
import { ILocation } from "../models/iLocation";
import { ICountry } from "../models/iCountry";
import { IAddress } from "../models/iAddress";
import { ICoordinates } from "../models/iCoordinates";

export const CUSTOM_ADDRESS_FORM_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AddressFormComponent),
  multi: true
};
@Component({
  selector: "fm-address-form",
  templateUrl: "./address-form.component.html",
  styleUrls: ["./address-form.component.scss"],
  providers: [CUSTOM_ADDRESS_FORM_VALUE_ACCESSOR]
})
export class AddressFormComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  constructor(
    protected mapService: MapService,
    protected fb: FormBuilder,
    protected mapsAPILoader: MapsAPILoader,
    protected ngZone: NgZone
  ) {}

  form: FormGroup;
  @Input()
  isNew = false;
  @Input()
  data: ILocation;
  @Input()
  isEditable = true;
  @Output()
  saveEmitter: EventEmitter<ILocation> = new EventEmitter();
  @Output()
  closeClickEmitter: EventEmitter<boolean> = new EventEmitter();
  @Input() isAccountingDisabled = false;
  @Input() countryLookupList: ICountry[] = [];
  @Input() standalone = true;

  saveButtonClicked = false;
  compareFn = this._compareFn.bind(this);

  protected ngInitUnsubscribe: Subject<void> = new Subject<void>();

  ngOnInit() {
    this.formInit();
    this.mapInit();
    this.data = null;
  }

  isInputDisabled() {
    return !this.isEditable ? "" : null;
  }

  ngOnChanges() {
    if (this.data && !this.isNew && !this.standalone) {
      this.fillForm(this.data);
      if (this.data.Coordinates) {
        this.mapService.setCoordinates(this.data.Coordinates);
      }
    } else {
      try {
        this.form.reset();
        // @ts-ignore
        document.getElementById("address").value = "";
        this.saveButtonClicked = false;
      } catch (error) {}
    }
  }

  ngOnDestroy() {
    this.ngInitUnsubscribe.next();
    this.ngInitUnsubscribe.complete();
  }

  formInit() {
    this.form = this.fb.group({
      GoogleString: [""],
      Title: ["", Validators.required],
      Address: this.fb.group({
        AddressLine1: ["", Validators.required],
        AddressLine2: [""],
        AddressLine3: [""],
        City: ["", Validators.required],
        Country: ["", Validators.required],
        State: [""],
        PostalCode: ["", Validators.required]
      }),
      Coordinates: this.fb.group({
        Longitude: [""],
        Latitude: [""]
      }),
      IsPrefered: [false],
      IsAccounting: [{ value: false, disabled: this.isAccountingDisabled }]
    });

    // if (this.standalone) {
    //   this.form.valueChanges.subscribe(data => this.save());
    // }
  }

  mapInit() {
    this.mapsAPILoader.load().then(() => {
      const element = <HTMLInputElement>document.getElementById("address");
      
      const autocomplete = new google.maps.places.Autocomplete(element, {
        types: ["address"]
      });

      autocomplete.addListener("place_changed", () => {
        this.ngZone.run(() => {
          // get the place result
          const place: google.maps.places.PlaceResult = autocomplete.getPlace();
          // verify result
          if (place.geometry === undefined || place.geometry === null) {
            return;
          }

          const newLocation: ILocation = {
            Title: this.form.controls["Title"].value,
            IsPrefered: this.form.controls["IsPrefered"].value,
            GoogleString: place.formatted_address,

            Address: {
              Country: {
                Value: place.address_components.find(f =>
                  f.types.some(d => d === "country")
                )
                  ? place.address_components.find(f =>
                      f.types.some(d => d === "country")
                    ).long_name
                  : "",
                IsoCode: place.address_components.find(f =>
                  f.types.some(d => d === "country")
                )
                  ? place.address_components.find(f =>
                      f.types.some(d => d === "country")
                    ).short_name
                  : ""
              },
              AddressLine1: place.address_components.find(f =>
                f.types.some(d => d === "route")
              )
                ? place.address_components.find(f =>
                    f.types.some(d => d === "route")
                  ).long_name
                : "",
              AddressLine2: place.address_components.find(f =>
                f.types.some(d => d === "street_number")
              )
                ? place.address_components.find(f =>
                    f.types.some(d => d === "street_number")
                  ).long_name
                : "",
              City: place.address_components.find(f =>
                f.types.some(
                  d =>
                    d === "locality" ||
                    d === "postal_town" ||
                    d === "sublocality"
                )
              )
                ? place.address_components.find(f =>
                    f.types.some(
                      d =>
                        d === "locality" ||
                        d === "postal_town" ||
                        d === "sublocality"
                    )
                  ).long_name
                : "",
              State: place.address_components.find(f =>
                f.types.some(d => d === "administrative_area_level_1")
              )
                ? place.address_components.find(f =>
                    f.types.some(d => d === "administrative_area_level_1")
                  ).long_name
                : "",
              PostalCode: place.address_components.find(f =>
                f.types.some(
                  d => d === "postal_code" || d === "postal_code_prefix"
                )
              )
                ? place.address_components.find(f =>
                    f.types.some(
                      d => d === "postal_code" || d === "postal_code_prefix"
                    )
                  ).long_name
                : ""
            },

            Coordinates: {
              Latitude: place.geometry.location.lat(),
              Longitude: place.geometry.location.lng()
            }
          };
          if (!this.standalone) {
            this.mapService.setCoordinates(newLocation.Coordinates);
          }
          this.fillForm(newLocation);
        });
      });
    });
  }

  addressManuallyModified() {
    this.form.get("Coordinates.Latitude").setValue(null);
    this.form.get("Coordinates.Longitude").setValue(null);
    if (this.standalone) {
      this.mapService.setCoordinates({ Longitude: null, Latitude: null });
    }
  }

  fillForm(data: ILocation) {
    if (!!data) {
      if (this.form) {
        // @ts-ignore
        document.getElementById("address").value = data.GoogleString;
        this.form.patchValue(data);
      }
    }
  }

  saveButtonEnabled() {
    try {
      if (this.isNew) {
        return !this.mapService
          .getLocations()
          .getValue()
          .map(v => v.Title)
          .includes(this.form.controls["Title"].value);
      } else {
        return !this.mapService
          .getLocations()
          .getValue()
          .map(v => v.Title)
          .filter(v => v !== this.data.Title)
          .includes(this.form.controls["Title"].value);
      }
    } catch (error) {
      return false;
    }
  }

  save() {
    this.saveButtonClicked = true;
    if (!this.form.valid) {
      return;
    }

    const newCoordinates: ICoordinates = {
      Longitude: this.form.controls["Coordinates"].value.Longitude,
      Latitude: this.form.controls["Coordinates"].value.Latitude
    };

    const newAddress: IAddress = {
      AddressLine1: this.form.controls["Address"].value.AddressLine1,
      AddressLine2: this.form.controls["Address"].value.AddressLine2,
      AddressLine3: this.form.controls["Address"].value.AddressLine3,
      City: this.form.controls["Address"].value.City,
      State: this.form.controls["Address"].value.State,
      Country: this.form.controls["Address"].value.Country,
      PostalCode: this.form.controls["Address"].value.PostalCode
    };
    // @ts-ignore
    let newLocation: ILocation = {
      Coordinates: newCoordinates,
      Address: newAddress,
      // @ts-ignore
      GoogleString: <HTMLInputElement>document.getElementById("address").value,
      Title: this.form.controls["Title"].value,
      IsPrefered: this.form.controls["IsPrefered"].value || false
    };

    if (!this.isAccountingDisabled) {
      newLocation = Object.assign({}, newLocation, {
        IsAccounting: this.form.controls["IsAccounting"].value || false
      });
    }
    this.value = newLocation;
    this.saveEmitter.emit(newLocation);
    this.closeClickEmitter.emit(false);
  }

  cancel() {
    this.closeClickEmitter.emit(false);
  }

  isTitleInValid() {
    const control = this.form.controls["Title"];
    return (
      !control.valid &&
      (control.touched || control.dirty || this.saveButtonClicked)
    );
  }

  checkIfInValid(controlName) {
    try {
      const control = this.form.get(`Address.${controlName}`);
      return (
        !control.valid &&
        (control.touched || control.dirty || this.saveButtonClicked)
      );
    } catch (error) {
      return false;
    }
  }

  _compareFn(a, b) {
    try {
      return a.IsoCode === b.IsoCode;
    } catch (error) {
      return false;
    }
  }

  // Functions for ngModel
  set value(data: ILocation) {
    if (this.data !== data) {
      this.data = data;
      this.onChange(data);
    }
  }

  get value(): ILocation {
    return this.data;
  }

  onChange = _ => {};
  onTouched = () => {};

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(val: ILocation): void {
    this.data = val;
    this.onChange(val);
  }

  setDisabledState?(isDisabled: boolean): void {}
}
